import { renderListGroup, type RenderListGroup } from './insightRenderLists'
/** Tolerant browser parsing for the page's native visual kinds, including standalone catalog consumers. */
import * as z from 'zod'
import {
  renderCard,
  renderCardSchemas,
  renderPresentationShell,
  renderSectionShell,
  renderPageShell,
  renderPageSectionShell,
  renderRelationships,
  renderCategoryBreakdown,
  type SupportedRenderCard,
  type RenderHelp,
  type RenderRelationships,
  type RenderCategoryBreakdown,
} from './insightRenderShapes'
import { InsightsFailure, renderIssue, throwRenderIssue, staticIssuePath, type RenderIssue } from './insightsFailure'
export type RenderErrorCard = { kind: 'render-error'; message: string; issue: RenderIssue }
export type RenderCard = SupportedRenderCard | RenderErrorCard
type RenderSection = { id: string; title: string; help: RenderHelp; cards: RenderCard[] }
export type RenderPresentation = { sections: RenderSection[]; unsupported?: true }
export type RenderPageSection =
  | RenderRelationships
  | RenderListGroup
  | RenderCategoryBreakdown
  | { kind: 'cards'; id: string; presentation: RenderPresentation }
  | { kind: 'render-error'; id: string; error: RenderErrorCard }
export type RenderPage = { sections: RenderPageSection[] }
const message = 'This insight presentation is not supported.'
function errorCard(issue: RenderIssue): RenderErrorCard {
  return { kind: 'render-error', message, issue }
}
export function parseRenderPresentation(
  raw: unknown,
  issues: RenderIssue[] = [],
  prefix: PropertyKey[] = [],
): RenderPresentation {
  const shell = renderPresentationShell.safeParse(raw)
  if (!shell.success) {
    issues.push(renderIssue(shell.error, prefix))
    return { sections: [], unsupported: true }
  }
  const sections = shell.data.sections.map((rawSection, sectionIndex): RenderSection => {
    const path = [...prefix, 'sections', sectionIndex]
    const section = renderSectionShell.safeParse(rawSection)
    if (!section.success) {
      const issue = renderIssue(section.error, path)
      issues.push(issue)
      return {
        id: `unsupported-${sectionIndex}`,
        title: 'Insight',
        help: { appliesTo: [], why: message, how: message, notes: [] },
        cards: [errorCard(issue)],
      }
    }
    const cards = section.data.cards.map((rawCard, index): RenderCard => {
      const parsed = renderCard.safeParse(rawCard)
      if (parsed.success) return parsed.data
      const identity = z.object({ kind: z.string() }).safeParse(rawCard)
      let issue = renderIssue(parsed.error, [...path, 'cards', index])
      if (identity.success && !(identity.data.kind in renderCardSchemas))
        issue = { ...issue, failureClass: 'unsupported_presentation', issueCode: 'unsupported_kind' }
      issues.push(issue)
      return errorCard(issue)
    })
    return { ...section.data, cards }
  })
  if (new Set(sections.map((section) => section.id)).size !== sections.length)
    throw new InsightsFailure('render_contract', 'duplicate_section_identity', staticIssuePath([...prefix, 'sections']))
  return { sections }
}
export function parseRenderPage(raw: unknown, issues: RenderIssue[] = []): RenderPage {
  const shell = renderPageShell.safeParse(raw)
  if (!shell.success) throwRenderIssue(shell.error, ['composition', 'page'])
  const sections = shell.data.sections.map((rawSection, index): RenderPageSection => {
    const path = ['composition', 'page', 'sections', index]
    const identity = renderPageSectionShell.safeParse(rawSection)
    if (!identity.success) throwRenderIssue(identity.error, path)
    const { id, kind } = identity.data
    if (kind === 'cards') {
      const section = z.object({ presentation: z.unknown() }).safeParse(rawSection)
      return {
        kind,
        id,
        presentation: parseRenderPresentation(section.success ? section.data.presentation : undefined, issues, [
          ...path,
          'presentation',
        ]),
      }
    }
    if (kind === 'list-group') {
      const group = renderListGroup.safeParse(rawSection)
      if (group.success) return group.data
      const issue = renderIssue(group.error, path)
      issues.push(issue)
      return { kind: 'render-error', id, error: errorCard(issue) }
    }
    if (kind === 'relationship-list') {
      const relationships = renderRelationships.safeParse(rawSection)
      if (relationships.success) return relationships.data
      const issue = renderIssue(relationships.error, path)
      issues.push(issue)
      return { kind: 'render-error', id, error: errorCard(issue) }
    }
    if (kind === 'category-breakdown') {
      const breakdown = renderCategoryBreakdown.safeParse(rawSection)
      if (breakdown.success) return breakdown.data
      const issue = renderIssue(breakdown.error, path)
      issues.push(issue)
      return { kind: 'render-error', id, error: errorCard(issue) }
    }
    const issue: RenderIssue = {
      failureClass: 'unsupported_presentation',
      issueCode: 'unsupported_kind',
      issuePath: staticIssuePath([...path, 'kind']),
    }
    issues.push(issue)
    return { kind: 'render-error', id, error: errorCard(issue) }
  })
  const ids = sections.map((section) => section.id)
  if (new Set(ids).size !== ids.length)
    throw new InsightsFailure('render_contract', 'duplicate_section_identity', 'composition.page.sections')
  return { sections }
}
